import * as Yup from 'yup'
import clsx from 'clsx'
import {useFormik} from 'formik'
import {useMutation} from '@apollo/client'
import {Link, useLocation} from 'react-router-dom'
import {useState} from 'react'
import {toast} from 'react-toastify'
import {defineMessages, IntlShape, useIntl} from 'react-intl'
import {toAbsoluteUrl} from '../../../../_metronic/helpers'
import {LanguageToggle} from '../../../../_metronic/partials/layout/header-menus/LanguageToggle'
import {VERSION_APP} from '../../helpers/consts'
import {REQUEST_PASSWORD_RESET} from '../../gql/mutations/authMutations'

const M = defineMessages({
  title: {id: 'auth.forgot.title', defaultMessage: 'Forgot password'},
  subtitle: {
    id: 'auth.forgot.subtitle',
    defaultMessage: 'Enter your email to receive a reset link.',
  },
  forcedNotice: {
    id: 'auth.forgot.forcedNotice',
    defaultMessage: 'You must change your password before signing in.',
  },
  sent: {
    id: 'auth.forgot.sent',
    defaultMessage: 'If the email exists, a reset link has been sent.',
  },
  submit: {id: 'auth.forgot.submit', defaultMessage: 'Send reset link'},

  // Compartidas por las tres pantallas de auth
  tagline: {id: 'auth.tagline', defaultMessage: 'Fast, Efficient and Productive'},
  backToLogin: {id: 'auth.backToLogin', defaultMessage: 'Back to login'},
  logoAlt: {id: 'auth.logoAlt', defaultMessage: 'Logo'},
  genericError: {
    id: 'auth.error.generic',
    defaultMessage: 'An error occurred. Please try again.',
  },

  // Reusadas
  email: {id: 'common.field.email', defaultMessage: 'Email'},
  pleaseWait: {id: 'admin.oneUser.pleaseWait', defaultMessage: 'Please wait...'},

  // Validacion
  emailInvalid: {id: 'validation.email.invalid', defaultMessage: 'Invalid email'},
  emailRequired: {id: 'validation.email.required', defaultMessage: 'Email is required'},
  minSymbols: {id: 'validation.minSymbols', defaultMessage: 'Minimum {min} symbols'},
  maxSymbols: {id: 'validation.maxSymbols', defaultMessage: 'Maximum {max} symbols'},
})

const EMAIL_MIN = 3
const EMAIL_MAX = 50

// El esquema necesita intl, asi que no puede vivir a nivel de modulo (§17.8).
const buildValidationSchema = (intl: IntlShape) =>
  Yup.object().shape({
    email: Yup.string()
      .email(intl.formatMessage(M.emailInvalid))
      .min(EMAIL_MIN, intl.formatMessage(M.minSymbols, {min: EMAIL_MIN}))
      .max(EMAIL_MAX, intl.formatMessage(M.maxSymbols, {max: EMAIL_MAX}))
      .required(intl.formatMessage(M.emailRequired)),
  })

export const ForgotPasswordPage = () => {
  const intl = useIntl()
  const defaultLogo = toAbsoluteUrl('/media/tfi/logo_policytools.png')
  const location = useLocation()
  const params = new URLSearchParams(location.search)
  const prefilledEmail = params.get('email') || ''
  const isForcedFlow = params.get('forced') === '1'

  const [requestReset, {loading}] = useMutation(REQUEST_PASSWORD_RESET)
  const [sent, setSent] = useState(false)

  const formik = useFormik({
    initialValues: {
      email: prefilledEmail,
    },
    validationSchema: buildValidationSchema(intl),
    onSubmit: async (values) => {
      try {
        const response = await requestReset({variables: {email: values.email}})
        const message =
          response.data?.requestPasswordReset?.message ||
          intl.formatMessage(M.sent)
        toast.success(message)
        setSent(true)
      } catch (error) {
        toast.error(intl.formatMessage(M.genericError))
        setSent(false)
      }
    },
  })

  return (
    <div className='d-flex flex-column flex-lg-row flex-column-fluid h-100'>
      <div className='d-flex flex-column flex-lg-row-fluid w-lg-50 p-10 order-2 order-lg-1'>
        <div className='d-flex flex-center flex-column flex-lg-row-fluid'>
          <div className='w-lg-500px p-10'>
            <form className='form w-100' onSubmit={formik.handleSubmit} noValidate>
              <div className='text-center mb-11'>
                <img
                  src='/media/tfi/logoNColorP.png'
                  width={200}
                  alt={intl.formatMessage(M.logoAlt)}
                />
                <h1 className='text-dark fw-bolder mb-3'>{intl.formatMessage(M.title)}</h1>
                <div className='text-gray-500 fw-semibold fs-6'>
                  {intl.formatMessage(M.subtitle)}
                </div>
              </div>

              {isForcedFlow && (
                <div
                  className='mb-6 alert'
                  style={{
                    backgroundColor: '#FFF4E5',
                    color: '#7A2E00',
                    border: '1px solid #C25100',
                  }}
                >
                  {intl.formatMessage(M.forcedNotice)}
                </div>
              )}

              {sent && (
                <div className='mb-10 bg-light-info p-8 rounded'>
                  <div className='text-info'>{intl.formatMessage(M.sent)}</div>
                </div>
              )}

              <div className='fv-row mb-8'>
                <label className='form-label fs-6 fw-bolder text-dark'>
                  {intl.formatMessage(M.email)}
                </label>
                <input
                  placeholder={intl.formatMessage(M.email)}
                  {...formik.getFieldProps('email')}
                  className={clsx(
                    'form-control bg-transparent',
                    {'is-invalid': formik.touched.email && formik.errors.email},
                    {
                      'is-valid': formik.touched.email && !formik.errors.email,
                    }
                  )}
                  type='email'
                  name='email'
                />
                {formik.touched.email && formik.errors.email && (
                  <div className='fv-plugins-message-container'>
                    <span role='alert' className='text-danger'>
                      {formik.errors.email}
                    </span>
                  </div>
                )}
              </div>

              <div className='d-grid mb-10'>
                <button
                  type='submit'
                  className='btn btn-primary mt-4'
                  disabled={formik.isSubmitting || !formik.isValid || loading}
                >
                  {!loading && (
                    <span className='indicator-label'>{intl.formatMessage(M.submit)}</span>
                  )}
                  {loading && (
                    <span className='indicator-progress' style={{display: 'block'}}>
                      {intl.formatMessage(M.pleaseWait)}
                      <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                    </span>
                  )}
                </button>
              </div>

              <div className='text-gray-500 text-center fw-semibold fs-6'>
                <Link to='/login' className='link-primary'>
                  {intl.formatMessage(M.backToLogin)}
                </Link>
              </div>
            </form>
          </div>
        </div>

        <div className='d-flex flex-center flex-wrap px-5 gap-3'>
          <LanguageToggle toggleBtnClass='btn-sm btn-active-light-primary border' />
          <div>
            <span>{VERSION_APP}</span>
          </div>
        </div>
      </div>

      <div
        className='d-flex flex-lg-row-fluid w-lg-50 bgi-size-cover bgi-position-center order-1 order-lg-2'
        style={{backgroundImage: `url(${toAbsoluteUrl('/media/misc/auth-bg.png')})`}}
      >
        <div className='d-flex flex-column flex-center py-15 px-5 px-md-15 w-100'>
          <span className='mb-12'>
            <img alt={intl.formatMessage(M.logoAlt)} src={defaultLogo} className='h-75px' />
          </span>
          <img
            className='mx-auto w-275px w-md-60 w-xl-500px mb-10 mb-lg-20'
            src={toAbsoluteUrl('/media/tfi/login.png')}
            alt=''
          />
          <h1 className='text-white fs-2qx fw-bolder text-center mb-7'>
            {intl.formatMessage(M.tagline)}
          </h1>
        </div>
      </div>
    </div>
  )
}
